import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  createDecimalNumberRegExp, parseDecimalNumber,
} from './useDecimalNumberInput.helpers';

type UseDecimalNumberInputProps = {
  maxDecimalPlaces: number;
  value: number | null;

  onChange: (value: number | null) => void;
};

type UseDecimalNumberInputResults = {
  inputValue: string;

  onInputChange: (newValue: string) => void;
};

/**
 * Handles a text input holding a non negative decimal number. The typed text is kept locally so that
 * values which are not fully typed yet ('1.', '1.50') are not rewritten by the parsed number.
 * Input that is not a valid decimal number, or exceeds `maxDecimalPlaces`, is rejected.
 */
export const useDecimalNumberInput = ({
  maxDecimalPlaces, value, onChange,
}: UseDecimalNumberInputProps): UseDecimalNumberInputResults => {
  const [inputValue, setInputValue] = useState(value?.toString() ?? '');
  const decimalNumberRegExp = useMemo(() => createDecimalNumberRegExp(maxDecimalPlaces), [maxDecimalPlaces]);

  // keeps the input in sync with value changes coming from the outside
  useEffect(() => {
    setInputValue(currentInputValue => parseDecimalNumber(currentInputValue) === value
      ? currentInputValue
      : value?.toString() ?? ''
    );
  }, [value]);

  const onInputChange = useCallback((newValue: string) => {
    if (!decimalNumberRegExp.test(newValue)) {
      return;
    }

    setInputValue(newValue);
    onChange(parseDecimalNumber(newValue));
  }, [decimalNumberRegExp, onChange]);

  return { inputValue, onInputChange };
};
